import React, { forwardRef, ReactElement, useEffect, useState } from 'react';
import { InputBase } from 'component-library/index';
import { regexValidation } from '../../../../utils/cob-utils';
import InputBaseOrangeAsteriskVariant from '../../../../component-library/atoms/InputBase/Variants/OrangeAsteriskVariant/InputBaseOrangeAsteriskVariant';
import SlideDown from '../../../SlideDown/SlideDown';

export interface IAddressProps {
    addressType?: string;
    className?: string;
    inputClass?: string;
    id?: string;
    errorText?: string;
    label?: string | ReactElement | ReactElement[];
    error?: boolean;
    required?: boolean;
    requiredMessage?: string;
    alternate?: boolean;
    maxLength?: number;
    invalidErrorMessage?: string;
    valid?: [boolean, (arg?: any) => void];
    value?: string;
    useSuccessIcon?: boolean;
    showSuccessIcon?: boolean;
    useOutsideValidation?: boolean;
    setValueCallback: THandlerFunction;
    onBlurCallback?: (((value: React.FocusEvent<HTMLInputElement>) => void) & React.FocusEventHandler<any>);
    onFocusCallback?: (((value: React.FocusEvent<HTMLInputElement>) => void) & React.FocusEventHandler<any>);
    onKeyDownCallback?: (((value: React.FocusEvent<HTMLInputElement>) => void) & React.KeyboardEventHandler<any>);
    disabled?: boolean;
    orangeAsterisksOnMobile?: boolean;
    disclaimerMessage?: string;
}

const AddressField = (({
    value = '',
    addressType = 'mailing',
    className,
    setValueCallback,
    onBlurCallback,
    onFocusCallback,
    onKeyDownCallback,
    useSuccessIcon,
    showSuccessIcon,
    useOutsideValidation,
    valid,
    orangeAsterisksOnMobile,
    disclaimerMessage,
    ...rest
}: IAddressProps, ref: React.Ref<any>) => {
    const [showIcon, setShowIcon] = useState(false);
    const [isFieldValid, setIsFieldValid] = useState(false);
    const [isBlurred, setIsBlurred] = useState(false);
    const regex = regexValidation();
    const validity = valid ?? [isFieldValid, setIsFieldValid];

    const validateField = () => {
        value = value ? value.trim() : '';
        return value &&
            regex.allow[`${addressType}_address`].test(value) &&
            !regex.disallow[`${addressType}_address`].test(value);
    };

    const focusHandler = (e: React.FocusEvent<HTMLInputElement>) => {
        if (e.type === 'blur') {
            onBlurCallback && onBlurCallback(e);
        } else if (e.type === 'focus') {
            onFocusCallback && onFocusCallback(e);
        }
        setIsBlurred(e.type !== 'focus');
        handleSuccessIcon(e.type === 'focus');
    };

    const handleSetValue = (e) => {
        if (!validity[0]) {
            validity[1](true);
        }
        setValueCallback && setValueCallback(e);
    };

    const handleSuccessIcon = (isActive: boolean = false) => {
        const isShowIcon = (useOutsideValidation ?? true) && validateField() && !isActive;
        setShowIcon(isShowIcon);
    };

    useEffect(() => {
        setIsBlurred(true);
    }, []);

    useEffect(() => {
        if (useOutsideValidation === undefined || !isBlurred)
            return;

        handleSuccessIcon(!useOutsideValidation);
    }, [value, useOutsideValidation]);

    return (
        <div className={`AddressField ${className ?? ''}`}>
            {!orangeAsterisksOnMobile &&
                <InputBase
                    {...rest}
                    ref={ref}
                    value={value}
                    setValue={handleSetValue}
                    onBlur={focusHandler}
                    onFocus={focusHandler}
                    onKeyDown={onKeyDownCallback}
                    isValid={validateField}
                    valid={validity}
                    useSuccessIcon={useSuccessIcon}
                    showSuccessIcon={showSuccessIcon !== undefined ? showSuccessIcon : showIcon}
                    showExclamationError
                />}
            {orangeAsterisksOnMobile &&
                <InputBaseOrangeAsteriskVariant
                    {...rest}
                    ref={ref}
                    value={value}
                    setValue={handleSetValue}
                    onBlur={focusHandler}
                    onFocus={focusHandler}
                    onKeyDown={onKeyDownCallback}
                    isValid={validateField}
                    valid={validity}
                    useSuccessIcon={useSuccessIcon}
                    showSuccessIcon={showSuccessIcon !== undefined ? showSuccessIcon : showIcon}
                    showExclamationError
                />}
            {disclaimerMessage &&
                <SlideDown
                    open={!isBlurred}
                    className='PersonalInformationForm__inputNote'
                    tabIndex={-1}
                >
                    {disclaimerMessage}
                </SlideDown>
            }
        </div>
    );
});

export default forwardRef(AddressField);